// Typed environment variable access
// Replaces raw process.env.REACT_APP_* usage throughout the app
// Vite exposes REACT_APP_* vars via the reactAppEnvPlugin in vite.config.js
//
// URL keys use getters so they always read the latest value from
// window.__CW_TENANT_URLS__ (populated after tenant init), avoiding
// stale build-time defaults when tenant URLs load after module import.
// Falls back to process.env.REACT_APP_* (Vite-transformed) for browser mode.
//
// Resolution order for every URL getter, including the derived ones:
//   1. runtime tenant URL (window.__CW_TENANT_URLS__.REACT_APP_*)
//   2. explicit .env value (process.env.REACT_APP_*)
//   3. only if neither is set — a value derived from genericUrl
// A URL that was configured on purpose (1 or 2) must never be overridden by a
// derived guess: the derivation assumes an apps./apps2. subdomain convention
// that not every tenant's infrastructure follows.

// Derives the apps2. host from genericUrl's services. host:
// https://services.X/core-services/graphql → https://apps2.X
// genericUrl is proven correct (used for all core API calls). Deriving from it
// avoids depending on REACT_APP_APP_SERVICE_URL, which was confirmed (via live
// diagnostic logging on tenant 801034) to actually be Admin's "App Services URL"
// field (a GraphQL endpoint) rather than the "Apps URL" root needed here.
function _apps2Base() {
  const generic = process.env.REACT_APP_genericUrl || "";
  const match = generic.match(/^(https?:\/\/)services\.(.+?)\/core-services\/graphql/);
  return match ? `${match[1]}apps2.${match[2]}` : "";
}

// Derives the apps. host from genericUrl's services. host — same pattern as
// _apps2Base above, but "apps." not "apps2.". PineLabs' backend lives here
// (e.g. https://apps.pre-prod.kwc.h02.cwcloud.in/); it hasn't moved to the
// CW Payment Hub the way Razorpay QR / CCAvenue have (see _paymentHubBase).
function _appsBase() {
  const generic = process.env.REACT_APP_genericUrl || "";
  const match = generic.match(/^(https?:\/\/)services\.(.+?)\/core-services\/graphql/);
  return match ? `${match[1]}apps.${match[2]}` : "";
}

// Derives the app-services base from genericUrl's services. host (same host,
// different path — unlike apps2. above): https://services.X/core-services/graphql
// → https://services.X/app-services/. Used only when neither a runtime tenant
// URL nor an explicit REACT_APP_PAYMENT_HUB_URL is configured.
function _paymentHubBase() {
  const generic = process.env.REACT_APP_genericUrl || "";
  const match = generic.match(/^(https?:\/\/services\.[^/]+)\/core-services\/graphql/);
  return match ? `${match[1]}/app-services/` : "";
}

const env = {
  // Server URLs — dynamic getters for tenant-aware resolution
  get serverUrl() {
    return process.env.REACT_APP_serverUrl || "";
  },
  get genericUrl() {
    return process.env.REACT_APP_genericUrl || "";
  },
  get signInUrl() {
    return process.env.REACT_APP_signInUrl || "";
  },
  get verifyOtpUrl() {
    return process.env.REACT_APP_verifyOtpUrl || "";
  },
  get domain() {
    return process.env.REACT_APP_domain || "";
  },
  get edcUrl() {
    return process.env.REACT_APP_edcUrl || "";
  },
  get apiUrl() {
    return process.env.REACT_APP_API_URL || "";
  },
  // PineLabs payment gateway URL — NOT migrated to the CW Payment Hub (see
  // paymentHubUrl below).
  // Resolution order (applies to every derived URL getter below): an explicitly
  // configured URL always wins — a runtime tenant URL (CW Browser / Admin) or an
  // explicit REACT_APP_* value in .env. Only when neither is set do we derive the
  // apps. host from genericUrl (see _appsBase above) — that derivation assumes a
  // subdomain convention (apps./apps2.) that not every tenant's infra follows, so
  // it must never override a URL someone configured on purpose.
  get pineLabsApiUrl() {
    const explicit = window.__CW_TENANT_URLS__?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
    if (explicit) return `${explicit.replace(/\/+$/, "")}/api/paymentGateway`;
    const appsBase = _appsBase();
    if (appsBase) return `${appsBase}/api/paymentGateway`;
    return "";
  },
  // CW Payment Hub (app-services) base — explicit config first (see resolution
  // order on pineLabsApiUrl), else derived from genericUrl's host (see
  // _paymentHubBase above). See src/lib/paymentHubUrls.js
  get paymentHubUrl() {
    const explicit = window.__CW_TENANT_URLS__?.REACT_APP_PAYMENT_HUB_URL || process.env.REACT_APP_PAYMENT_HUB_URL;
    if (explicit) return explicit;
    const derived = _paymentHubBase();
    if (derived) return derived;
    return "";
  },
  // Explicit config first (see resolution order on pineLabsApiUrl). Else derive
  // from signInUrl (.../auth/signin -> .../auth/refreshToken) or, failing that,
  // genericUrl's host (same pattern as _paymentHubBase). Without this fallback,
  // a build/tenant that never set REACT_APP_REFRESHTOKEN_URL got "" here forever
  // — every access-token expiry skipped straight to a forced sign-out instead of
  // a silent refresh (confirmed live: SIGNIN-REDIRECT-LOOP KWC/GPR /sign-in redirect loop).
  get refreshTokenUrl() {
    const explicit = window.__CW_TENANT_URLS__?.REACT_APP_REFRESHTOKEN_URL || process.env.REACT_APP_REFRESHTOKEN_URL;
    if (explicit) return explicit;
    const signIn = process.env.REACT_APP_signInUrl || "";
    if (signIn.includes("/auth/signin")) return signIn.replace("/auth/signin", "/auth/refreshToken");
    const generic = process.env.REACT_APP_genericUrl || "";
    const match = generic.match(/^(https?:\/\/services\.[^/]+)\/core-services\/graphql/);
    if (match) return `${match[1]}/core-services/auth/refreshToken`;
    return "";
  },

  // SSO / OAuth
  get ssoUrl() {
    return process.env.REACT_APP_SSOURL || "";
  },
  get redirectUrl() {
    return process.env.REACT_APP_redirectURL || "";
  },

  // File upload — explicit config first (see resolution order on pineLabsApiUrl),
  // else apps2. host derived from genericUrl (see _apps2Base above)
  get uploadDocumentUrl() {
    const explicit = window.__CW_TENANT_URLS__?.REACT_APP_uploadDocumentUrl || process.env.REACT_APP_uploadDocumentUrl;
    if (explicit) return explicit;
    const appsBase = _apps2Base();
    if (appsBase) return appsBase + "/api/uploadImages";
    return "";
  },

  // Print template download — explicit config first, else same host as
  // genericUrl with /graphql → /print/downloadPrintTemplates
  get downloadPrintTemplatesUrl() {
    const explicit = window.__CW_TENANT_URLS__?.REACT_APP_downloadPrintTemplatesUrl || process.env.REACT_APP_downloadPrintTemplatesUrl;
    if (explicit) return explicit;
    const generic = process.env.REACT_APP_genericUrl || "";
    if (generic) return generic.replace(/\/graphql$/, "/print/downloadPrintTemplates");
    return "";
  },

  // Sentry (non-URL, static)
  sentryDsn: process.env.REACT_APP_SENTRY_DSN || "",
  sentryEnabled: process.env.REACT_APP_ENABLE_SENTRY === "true",
  sentryEnvironment: process.env.REACT_APP_Sentry_Environment || "development",

  // Feature Flags (non-URL, static)
  instanceType: process.env.REACT_APP_INSTANCE_TYPE || "Enterprise",
  multilingual: process.env.REACT_APP_MULTILINGUAL === "true",
  enableVision: process.env.REACT_APP_ENABLE_VISION === "true",
  ofcScanMode: process.env.REACT_APP_OFC_SCAN_MODE === "true",
  disableAggregatorInvoicePrint: process.env.REACT_APP_DISABLE_AGGREGATOR_INVOICE_PRINT === "true",
  disableAggregatorKotPrint: process.env.REACT_APP_DISABLE_AGGREGATOR_KOT_PRINT === "true",
  printDayEndReport: process.env.REACT_APP_PRINT_DAY_END_REPORT === "true",

  // UI Customization (non-URL, static)
  posBackgroundColor: process.env.REACT_APP_POSBACKGROUND_COLOR || "",
  language: process.env.REACT_APP_LANGUAGE || "English",

  // Versioning — single source of truth: package.json → VITE_APP_VERSION
  releaseNo: import.meta.env.VITE_APP_VERSION || "1.0.0",
  // Commit baked in at build time — see window.__CW_BUILD__ in sw-register.js
  gitSha: import.meta.env.VITE_GIT_SHA || "unknown",

  // WebSocket
  get wsUrl() {
    return process.env.REACT_APP_WS_URL || "";
  },
  get socketServerUrl() {
    return process.env.REACT_APP_SOCKET_SERVER_URL || "";
  },

  // Misc (non-URL, static)
  diffHoursDifferentDate: parseInt(process.env.REACT_APP_DIFF_HOURS_DIFFERENT_DATE || "2", 10),
  diffHoursSameDate: parseInt(process.env.REACT_APP_DIFF_HOURS_SAME_DATE || "3", 10),
  sproutzVerifyStock: process.env.REACT_APP_SPROUTZ_VERIFYSTOCK || "",
  // Explicit config first (see resolution order on pineLabsApiUrl), else apps2.
  // host derived from genericUrl
  get dayEndApplicationUrl() {
    const explicit = window.__CW_TENANT_URLS__?.REACT_APP_DAY_END_APPLICATION_URL || process.env.REACT_APP_DAY_END_APPLICATION_URL;
    if (explicit) return explicit;
    const appsBase = _apps2Base();
    if (appsBase) return appsBase + "/apps/dayEndProcess?appId=7547FC9803144B9CA04C7DE35C3B087F";
    return "";
  },
};

export default env;
